import { logger } from "../logger";

/**
 * What a screen gets back when proving identity takes the user off the page
 * (FR-14, SEC-4).
 *
 * The MSAL provider re-authenticates by REDIRECT: `acquireTokenRedirect` leaves
 * the document, so a draft held in React state, the body the server refused and
 * the user's place in the app are all gone by the time the identity provider
 * sends them back. Holding them here is what makes the dialog's promise — the
 * save continues by itself — true on the path that ships to customers, rather
 * than only under the dev provider, which re-mints in place.
 *
 * sessionStorage, not localStorage: the entry can carry a credential the user
 * has typed and not yet saved, so it dies with the tab the way the token does
 * (SEC-14). It is removed the moment it is read — `take` deletes before it
 * parses — so a rehydrate that throws still leaves nothing behind, and nothing
 * here is ever logged beyond the fact that an entry existed.
 *
 * Four things bound the entry's life, because a tab outlives a session: the
 * five-minute age below, `clearReauthHandoff` on sign-out (`AuthContext`), the
 * account it was staged for and the instance it was staged against. The last
 * two are the ones that matter when the return trip never completes. The tab
 * stays on the identity provider, and either a DIFFERENT person signs in on it
 * — without the account check their first visit to the staging screen would
 * restore and replay the previous person's draft, their credential, audited as
 * the new user — or the same person comes back and switches instance before
 * reaching the screen, in which case the replay would write one instance's
 * credential into another instance's binding. The body carries no instance of
 * its own; the header does, and the header is built from whatever is selected
 * at replay time, so the entry has to remember which instance it meant.
 */
const STORAGE_KEY = "skydata-reauth-handoff";

/**
 * The server's re-authentication window is five minutes, so an entry that
 * outlives it would be replayed straight into the 401 it was carried to avoid.
 * An abandoned entry is dropped on the next read rather than lingering with a
 * credential in it.
 */
const MAX_AGE_MS = 5 * 60 * 1000;

interface StoredHandoff {
  /** Where in the app to put the user down again, once signed in. */
  returnTo: string;
  /** Who staged it. An entry is never handed to a different account. */
  account: string | null;
  /** Which instance it was staged against. Never replayed into another one. */
  instanceId: string | null;
  savedAt: number;
  /** The screen's own state, opaque here: only the screen knows its shape. */
  draft: unknown;
  /** The request body the server refused, replayed byte for byte. */
  body: unknown;
}

export interface CarriedHandoff {
  draft: unknown;
  body: unknown;
}

/**
 * An in-app path and not somewhere else. `returnTo` is read back after a trip
 * through an identity provider, which is exactly the shape an open redirect
 * takes, so a protocol-relative or absolute address is refused rather than
 * followed.
 */
function isAppPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//");
}

/**
 * The identity provider decides the casing of the address it hands back, and it
 * is not obliged to hand back the casing the directory was seeded with: the same mailbox can
 * return `Ada.Lovelace@…` on one sign-in and `ada.lovelace@…` on the next.
 * Compared verbatim that reads as a different person, and the entry the user
 * was sent away to save would be silently thrown away. An address is
 * case-insensitive for the part that matters here, so both sides are folded.
 */
function sameAccount(a: string | null, b: string | null): boolean {
  return (a?.trim().toLowerCase() ?? null) === (b?.trim().toLowerCase() ?? null);
}

export function clearReauthHandoff(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode — nothing was held in the first place */
  }
}

/**
 * `account` and `instanceId` are who is signed in NOW and what they have
 * selected NOW. An entry staged by anyone else, staged while signed in and read
 * while signed out, or staged against another instance is dropped rather than
 * returned, and dropped means removed: a credential nobody may use is a
 * credential that has no reason to stay in storage.
 */
function read(account: string | null, instanceId: string | null): StoredHandoff | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredHandoff;
    const fresh =
      typeof parsed?.savedAt === "number" &&
      Date.now() - parsed.savedAt < MAX_AGE_MS &&
      typeof parsed.returnTo === "string";
    if (
      !fresh ||
      !sameAccount(parsed.account ?? null, account) ||
      (parsed.instanceId ?? null) !== instanceId
    ) {
      clearReauthHandoff();
      return null;
    }
    return parsed;
  } catch {
    clearReauthHandoff();
    return null;
  }
}

/** Held BEFORE the provider is asked, because a redirecting one never returns. */
export function stashReauthHandoff(entry: {
  returnTo: string;
  account: string | null;
  instanceId: string | null;
  draft: unknown;
  body: unknown;
}): void {
  const stored: StoredHandoff = {
    returnTo: isAppPath(entry.returnTo) ? entry.returnTo : "/",
    account: entry.account,
    instanceId: entry.instanceId,
    savedAt: Date.now(),
    draft: entry.draft,
    body: entry.body,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private mode, or a draft past the quota. The user signs in and saves
    // again; nothing was written to the server either way.
    logger.info("re-authentication handoff could not be held");
  }
}

/** Where the redirect should land, without consuming the entry the screen needs. */
export function reauthReturnTo(account: string | null, instanceId: string | null): string | null {
  const stored = read(account, instanceId);
  if (!stored || !isAppPath(stored.returnTo)) return null;
  return stored.returnTo;
}

/**
 * Read once and forget. Calling it twice — a remount, React's double-invoked
 * effects — yields null the second time, which is what makes the auto-retry
 * happen exactly once.
 */
export function takeReauthHandoff(
  account: string | null,
  instanceId: string | null,
): CarriedHandoff | null {
  const stored = read(account, instanceId);
  clearReauthHandoff();
  if (!stored) return null;
  return { draft: stored.draft, body: stored.body };
}
